// Simple password hashing utility for number passwords
// Using Web Crypto API for secure hashing, with fallback for HTTP contexts

// Simple hash fallback for non-secure contexts (HTTP)
function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
}

export async function hashPassword(number, salt) {
    const inputString = number.toString() + salt;
    
    // Check if crypto.subtle is available (HTTPS context)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
        try {
            const encoder = new TextEncoder();
            const data = encoder.encode(inputString);
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            return hashHex;
        } catch (error) {
            console.warn('Crypto.subtle failed, using fallback hash:', error);
        }
    }
    
    // Fallback for non-secure contexts (HTTP on mobile)
    // This is less secure but works for our simple number password game
    return simpleHash(inputString);
}

export function generateSalt() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function verifyPassword(number, salt, hash) {
    const inputString = number.toString() + salt;
    
    // First try with the current context's hash method
    const computedHash = await hashPassword(number, salt);
    if (computedHash === hash) {
        return true;
    }
    
    // If that fails and we're in HTTP context, try SHA-256 hash
    // (user might have been created in HTTPS context)
    if (!crypto.subtle) {
        try {
            // Try to compute SHA-256 hash using a polyfill approach
            // Since we can't use crypto.subtle, we'll just check if the hash length matches SHA-256
            // SHA-256 produces 64 character hex string, our simple hash is much shorter
            if (hash.length === 64) {
                // This is likely a SHA-256 hash, but we can't verify it in HTTP context
                console.warn('Cannot verify SHA-256 hash in non-secure context. User may need to reset password.');
                return false;
            }
        } catch (error) {
            console.error('Error in password verification:', error);
        }
    }
    
    // If we're in HTTPS context, also try the simple hash
    // (user might have been created in HTTP context)
    if (crypto.subtle) {
        const simpleHashValue = simpleHash(inputString);
        if (simpleHashValue === hash) {
            return true;
        }
    }
    
    return false;
}

// Generate array of 9 numbers with shuffled positions
export function generateNumberOptions(correctNumber) {
    // Create an array with 9 fixed numbers including the correct one
    const numbers = [];
    
    // Always include the correct number
    numbers.push(correctNumber);
    
    // Generate 8 other unique numbers
    const otherNumbers = new Set();
    while (otherNumbers.size < 8) {
        const num = Math.floor(Math.random() * 100) + 1;
        if (num !== correctNumber) {
            otherNumbers.add(num);
        }
    }
    
    // Combine all numbers
    numbers.push(...Array.from(otherNumbers));
    
    // Fisher-Yates shuffle for better randomization
    for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
    }
    
    return numbers;
}